

import { useState, useEffect, useCallback, useRef } from 'react'
import { useShow } from '@/contexts/ShowContext'
import { useNavigate } from 'react-router-dom'
import { apiListTimelines, apiCreateTimeline, apiDeleteTimeline, apiLoadTimeline } from '@/components/TimelineEditor/api'
import { DEFAULT_TRACKS } from '@/components/TimelineEditor/store'
import { useModal } from '@/contexts/ModalContext'
import ConfirmModal from '@/components/modals/ConfirmModal'
import { UserRole, useUser } from '@/contexts/UserContext'
import Button from '@/components/ui/Button'
import Loader from '@/components/loader'
import { FiPlus, FiCopy, FiMoreHorizontal, FiTrash, FiChevronUp, FiChevronDown, FiSearch } from 'react-icons/fi'
import { FaAngleLeft, FaAngleRight, FaAngleDoubleLeft } from 'react-icons/fa'
import { HiOutlineDuplicate } from 'react-icons/hi'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import dayjs from 'dayjs'

export default function TimelinesPage() {
  const { show } = useShow()
  const { user } = useUser()
  const navigate = useNavigate()
  const { openModal } = useModal()
  const [timelines, setTimelines] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sortKey, setSortKey] = useState('updatedAt')
  const [sortDir, setSortDir] = useState('desc')
  const [ownOnly, setOwnOnly] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const pageSize = 25
  const debounceRef = useRef(null)

  const fetchTimelines = useCallback(async (sort = sortKey, dir = sortDir, own = ownOnly, q = search, p = page) => {
    try {
      const res = await apiListTimelines(show._id, { sortBy: sort, sortDir: dir, own, search: q, page: p, size: pageSize })
      setTimelines(res.data)
      setTotalPages(res.totalPages)
      setTotalCount(res.totalCount)
    } catch (e) {
      setError(e.message)
    }
  }, [show?._id, sortKey, sortDir, ownOnly, search, page])

  const handleSort = (key) => {
    const newDir = sortKey === key ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'
    setSortKey(key)
    setSortDir(newDir)
    setPage(1)
    fetchTimelines(key, newDir, ownOnly, search, 1)
  }

  const handleToggleOwn = (checked) => {
    setOwnOnly(checked)
    setPage(1)
    fetchTimelines(sortKey, sortDir, checked, search, 1)
  }

  const handleSearch = (value) => {
    setSearch(value)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPage(1)
      fetchTimelines(sortKey, sortDir, ownOnly, value, 1)
    }, 300)
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    fetchTimelines(sortKey, sortDir, ownOnly, search, newPage)
  }

  useEffect(() => {
    return () => clearTimeout(debounceRef.current)
  }, [])

  useEffect(() => {
    if (!show?._id) return
    setLoading(true)
    fetchTimelines().finally(() => setLoading(false))
  }, [show?._id])

  const handleCreate = async () => {
    try {
      const timeline = await apiCreateTimeline(show._id, {
        name: 'New Timeline',
        tracks: DEFAULT_TRACKS,
        clips: [],
      })
      navigate(`/show/timelines/${timeline._id}`)
    } catch (e) {
      setError(e.message)
    }
  }

  const handleCopyId = (e, timeline) => {
    e.stopPropagation()
    navigator.clipboard.writeText(timeline._id)
  }

  const duplicateTimeline = async (timeline) => {
    setLoading(true)
    try {
      const sourceTimeline = await apiLoadTimeline(show._id, timeline._id)
      await apiCreateTimeline(show._id, {
        name: `${sourceTimeline.name} (Copy)`,
        tracks: sourceTimeline.tracks || DEFAULT_TRACKS,
        clips: sourceTimeline.clips || [],
        assets: sourceTimeline.assets || {},
        assetsFolder: sourceTimeline.assetsFolder || [],
        mutedTracks: sourceTimeline.mutedTracks || {},
        trackVolumes: sourceTimeline.trackVolumes || {},
        episode: sourceTimeline.episode || undefined,
        segment: sourceTimeline.segment || undefined,
      })
      await fetchTimelines()
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const handleDuplicate = async (e, timeline) => {
    e.stopPropagation()
    await duplicateTimeline(timeline)
  }

  const deleteTimeline = async (timeline) => {
    try {
      await apiDeleteTimeline(show._id, timeline._id)
      await fetchTimelines()
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const handleDelete = async (e, timeline) => {
    e.stopPropagation()
    await openModal(
      <ConfirmModal
        type="destructive"
        title="Delete Timeline"
        message={`Are you sure you want to delete "${timeline.name}"?`}
        buttons={[
          {
            label: 'Delete',
            type: 'danger',
            onClick: async () => deleteTimeline(timeline),
          },
        ]}
      />
    )
  }

  if (loading) return <Loader />

  if (error) {
    return (
      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
        <span className="text-red-400">Error: {error}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex justify-between items-center px-10 h-20 ">
        <div className="flex gap-2 items-center">
          <h1 className="text-2xl font-bold border-l-4 border-primary pl-4">Timelines</h1>
        </div>
        <div className="flex gap-2 items-center">
          <UserRole role="editor">
            <Button type="secondary" icon={<FiPlus className="w-4 h-4" />} onClick={handleCreate}>
              New Timeline
            </Button>
          </UserRole>
        </div>
      </div>

      <div className="flex items-center gap-4 px-10 py-3">
        <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 w-64">
          <FiSearch className="w-4 h-4 text-secondary-foreground/50" />
          <input
            type="text"
            placeholder="Search timelines..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="bg-transparent outline-none text-sm flex-1"
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="own-filter" checked={ownOnly} onCheckedChange={handleToggleOwn} />
          <label htmlFor="own-filter" className="text-xs text-secondary-foreground cursor-pointer select-none">
            My Timelines
          </label>
        </div>
      </div>

      <div className="flex flex-col flex-1 overflow-y-auto">
        {timelines.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-4 text-secondary-foreground">
            <p>No timelines yet.</p>
            <UserRole role="editor">
              <Button type="secondary" icon={<FiPlus className="w-4 h-4" />} onClick={handleCreate}>
                Create your first timeline
              </Button>
            </UserRole>
          </div>
        ) : (
          <Table>
            <TableHeader className="bg-card text-secondary-foreground">
              <TableRow>
                {[
                  { key: 'name', label: 'Name', className: 'pl-10' },
                  { key: 'user', label: 'User' },
                  { key: 'createdAt', label: 'Created' },
                  { key: 'updatedAt', label: 'Last Updated' },
                ].map((col) => (
                  <TableHead
                    key={col.key}
                    className={`cursor-pointer select-none hover:text-foreground text-secondary-foreground/70 ${col.className || ''}`}
                    onClick={() => handleSort(col.key)}
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      {sortKey === col.key && (
                        sortDir === 'asc'
                          ? <FiChevronUp className="w-3 h-3" />
                          : <FiChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                ))}
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {timelines.map((timeline) => (
                <TableRow
                  key={timeline._id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/show/timelines/${timeline._id}`)}
                >
                  <TableCell className="pl-10">
                    <div className="flex flex-col gap-1">
                      <div className="text-secondary-foreground">{timeline.name}</div>
                      {timeline.segment && (
                        <div className="text-xs text-secondary-foreground/50">
                          {timeline.episode.name}{timeline.segment ? ` / ${timeline.segment.name}` : ''}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-secondary-foreground/70 text-xs">
                    {timeline.user ? `${timeline.user.first_name} ${timeline.user.last_name}` : '—'}
                  </TableCell>
                  <TableCell className="text-secondary-foreground/70 text-xs">
                    {dayjs(timeline.createdAt).format('MMM D, YYYY h:mm A')}
                  </TableCell>
                  <TableCell className="text-secondary-foreground/70 text-xs">
                    {dayjs(timeline.updatedAt).format('MMM D, YYYY h:mm A')}
                  </TableCell>
                  <TableCell className="pr-10">
                    <DropdownMenu>
                      <DropdownMenuTrigger onClick={(e) => e.stopPropagation()}>
                        <div className="p-2 rounded-md text-secondary-foreground/50 hover:text-secondary-foreground hover:bg-secondary">
                          <FiMoreHorizontal className="w-4 h-4" />
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => handleCopyId(e, timeline)}>
                          <FiCopy className="w-4 h-4" />
                          Copy ID
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => handleDuplicate(e, timeline)}>
                          <HiOutlineDuplicate className="w-4 h-4" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => handleDelete(e, timeline)}
                          className="text-red-500 focus:text-primary-foreground group"
                          disabled={timeline.user?._id !== user?.id}
                        >
                          <FiTrash className="w-4 h-4 text-red-500 group-hover:text-primary-foreground" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-2 px-10 py-3 border-t text-xs text-secondary-foreground">
          <Button type="secondary" size="sm" icon={<FaAngleDoubleLeft className="w-4 h-4" />} onClick={() => handlePageChange(1)} disabled={page === 1} />
          <Button type="secondary" size="sm" icon={<FaAngleLeft className="w-4 h-4" />} onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
          <span>{page}/{totalPages}</span>
          <Button type="secondary" size="sm" icon={<FaAngleRight className="w-4 h-4" />} onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} />
          <span>{`${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalCount)} of ${totalCount}`}</span>
        </div>
      )}
    </div>
  )
}
